import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { QUERY_PARAMS } from '../../models/consts/QueryParams.const';
import { ROUTE_PATH, URL_PARAMETER } from '../../routing/RoutePath.const';
import { ContactEmail } from '@libs/index';
import { StringUtils } from '@libs/index';

@Injectable({
  providedIn: 'root',
})
export class NavigationService {
  constructor(private router: Router) {}

  // WARNING: url will not be checked
  public forceRedirectToUrl(url: string): void {
    window.location.href = url;
  }

  public redirectToUrl(url: string): void {
    window.location.href = this.checkAndCorrectDomainOfUrl(url);
  }

  public replaceByUrl(url: string): void {
    window.location.replace(this.checkAndCorrectDomainOfUrl(url));
  }

  public back(): void {
    if (window.history.length) {
      window.history.go(-1);
    } else {
      this.navigateToHome();
    }
  }

  // region Route paths
  public get publicHomePath(): string {
    return `/${ROUTE_PATH.HOME}`;
  }

  public get publicImprintPath(): string {
    return `/${ROUTE_PATH.IMPRINT}`;
  }

  public get publicPrivacyPolicyPath(): string {
    return `/${ROUTE_PATH.PRIVACY_POLICY}`;
  }

  public get publicSalonSearchResultPath(): string {
    return `/${ROUTE_PATH.SALON_SEARCH_RESULT}`;
  }

  public get publicSalonDetailsPath(): string {
    return `/${ROUTE_PATH.SALON_DETAILS}`;
  }

  public get publicSalonBookingConfigPath(): string {
    return `/${ROUTE_PATH.SALON_BOOKING_CONFIG}`;
  }

  public get publicTopSalonsPath(): string {
    return `/${ROUTE_PATH.TOP_SALONS}`;
  }

  public get publicTopSalonsCityPath(): string {
    return `/${ROUTE_PATH.TOP_SALONS_CITY}`;
  }

  public get publicGoogleSalonDetailsPath(): string {
    return `/${ROUTE_PATH.GOOGLE_SALON_DETAILS}`;
  }

  public get publicAppointmentDetailsPath(): string {
    return `/${ROUTE_PATH.PUBLIC_APPOINTMENT_DETAILS}`;
  }

  public get redirectLinkBookingPath(): string {
    return `/${ROUTE_PATH.REDIRECT_LINK}/${ROUTE_PATH.BOOKING_DIRECT_LINK}`;
  }

  public get redirectLinkQrCheckinPath(): string {
    return `/${ROUTE_PATH.REDIRECT_LINK}/${ROUTE_PATH.QR_CHECKIN_LINK}`;
  }

  public get authLoginPath(): string {
    return `/${ROUTE_PATH.AUTH_PROCESS}/${ROUTE_PATH.LOGIN}`;
  }

  public get authLoginSecretWithPinPath(): string {
    return `/${ROUTE_PATH.AUTH_PROCESS}/${ROUTE_PATH.LOGIN_SECRET_WITH_PIN}`;
  }

  public get authLoginSecretWithPasswordPath(): string {
    return `/${ROUTE_PATH.AUTH_PROCESS}/${ROUTE_PATH.LOGIN_SECRET_WITH_PASSWORD}`;
  }

  public get authRegistrationPersonalDataPath(): string {
    return `/${ROUTE_PATH.AUTH_PROCESS}/${ROUTE_PATH.REGISTRATION_PERSONAL_DATA}`;
  }

  public get authRegistrationSecretDataPath(): string {
    return `/${ROUTE_PATH.AUTH_PROCESS}/${ROUTE_PATH.REGISTRATION_SECRET_DATA}`;
  }

  public get authRegistrationPinPath(): string {
    return `/${ROUTE_PATH.AUTH_PROCESS}/${ROUTE_PATH.REGISTRATION_PIN}`;
  }

  public get authRegistrationSuccessPath(): string {
    return `/${ROUTE_PATH.AUTH_PROCESS}/${ROUTE_PATH.REGISTRATION_SUCCESS}`;
  }

  public get feedbackEmotionPath(): string {
    return `/${ROUTE_PATH.FEEDBACK_PROCESS}/${ROUTE_PATH.FEEDBACK_EMOTION}`;
  }

  public get feedbackInternalDetailsPath(): string {
    return `/${ROUTE_PATH.FEEDBACK_PROCESS}/${ROUTE_PATH.FEEDBACK_INTERNAL_DETAILS}`;
  }

  public get feedbackInternalSuccessPath(): string {
    return `/${ROUTE_PATH.FEEDBACK_PROCESS}/${ROUTE_PATH.FEEDBACK_INTERNAL_SUCCESS}`;
  }

  public get privateSalonVisitedPath(): string {
    return `/${ROUTE_PATH.PRIVATE}/${ROUTE_PATH.SALON_VISITED}`;
  }

  public get privateQrCheckinConfigPath(): string {
    return `/${ROUTE_PATH.PRIVATE}/${ROUTE_PATH.QR_CHECKIN_CONFIG}`;
  }

  public get privateQrCheckinSuccessPath(): string {
    return `/${ROUTE_PATH.PRIVATE}/${ROUTE_PATH.QR_CHECKIN_SUCCESS}`;
  }

  public get privateAppointmentHistoryPath(): string {
    return `/${ROUTE_PATH.PRIVATE}/${ROUTE_PATH.APPOINTMENT_HISTORY}`;
  }

  public get privateAppointmentDetailsPath(): string {
    return `/${ROUTE_PATH.PRIVATE}/${ROUTE_PATH.APPOINTMENT_DETAILS}`;
  }

  public get privateUserProfilePath(): string {
    return `/${ROUTE_PATH.PRIVATE}/${ROUTE_PATH.USER_PROFILE}`;
  }

  // endregion

  // region Checking functions

  public isPrivateRoute(path: string): boolean {
    return path?.startsWith(`/${ROUTE_PATH.PRIVATE}`);
  }

  public isUserProfileRoute(path: string): boolean {
    return path?.startsWith(this.privateUserProfilePath);
  }

  public isSalonVisitedRoute(path: string): boolean {
    return path?.startsWith(this.privateSalonVisitedPath);
  }

  public isAppointmentHistoryRoute(path: string): boolean {
    return path?.startsWith(this.privateAppointmentHistoryPath);
  }

  public isHomePage(path: string): boolean {
    return path?.startsWith(this.publicHomePath);
  }

  // endregion

  // region AUTH PROCESS
  public navigateToLogin(
    redirectLink?: string,
    email?: string,
    phoneNr?: string
  ): Promise<boolean> {
    const queryParams = {
      [QUERY_PARAMS.REDIRECT_LINK]: redirectLink || undefined,
      [QUERY_PARAMS.EMAIL]: email || undefined,
      [QUERY_PARAMS.PHONE_NUMBER]: phoneNr || undefined,
    };

    return this.router.navigate([this.authLoginPath], {
      queryParams: queryParams,
    });
  }

  public updateLoginUrl(
    redirectLink?: string,
    email?: string,
    phoneNr?: string
  ): Promise<boolean> {
    const queryParams = {
      [QUERY_PARAMS.REDIRECT_LINK]: redirectLink || undefined,
      [QUERY_PARAMS.EMAIL]: email || undefined,
      [QUERY_PARAMS.PHONE_NUMBER]: phoneNr || undefined,
    };

    return this.router.navigate([this.authLoginPath], {
      queryParams,
      replaceUrl: true,
    });
  }

  public navigateToLoginSecretWithPassword(
    redirectLink?: string,
    email?: string,
    phoneNr?: string
  ): Promise<boolean> {
    const queryParams = {
      [QUERY_PARAMS.REDIRECT_LINK]: redirectLink || undefined,
      [QUERY_PARAMS.EMAIL]: email || undefined,
      [QUERY_PARAMS.PHONE_NUMBER]: phoneNr || undefined,
    };

    return this.router.navigate([this.authLoginSecretWithPasswordPath], {
      queryParams,
    });
  }

  public navigateToLoginSecretWithPin(
    challengeId: string,
    redirectLink?: string,
    email?: string,
    phoneNr?: string,
    passwordToUpdate?: string
  ): Promise<boolean> {
    const queryParams = {
      [QUERY_PARAMS.LOGIN_CHALLENGE_ID]: challengeId || undefined,
      [QUERY_PARAMS.REDIRECT_LINK]: redirectLink || undefined,
      [QUERY_PARAMS.EMAIL]: email || undefined,
      [QUERY_PARAMS.PHONE_NUMBER]: phoneNr || undefined,
      [QUERY_PARAMS.ENCODED_PASSWORD_TO_UPDATE]: passwordToUpdate || undefined,
    };

    return this.router.navigate([this.authLoginSecretWithPinPath], {
      queryParams,
    });
  }

  public updateLoginSecretWithPin(
    challengeId: string,
    redirectLink?: string,
    email?: string,
    phoneNr?: string,
    passwordToUpdate?: string
  ): Promise<boolean> {
    const queryParams = {
      [QUERY_PARAMS.LOGIN_CHALLENGE_ID]: challengeId || undefined,
      [QUERY_PARAMS.REDIRECT_LINK]: redirectLink || undefined,
      [QUERY_PARAMS.EMAIL]: email || undefined,
      [QUERY_PARAMS.PHONE_NUMBER]: phoneNr || undefined,
      [QUERY_PARAMS.ENCODED_PASSWORD_TO_UPDATE]: passwordToUpdate || undefined,
    };

    return this.router.navigate([this.authLoginSecretWithPinPath], {
      queryParams,
      replaceUrl: true,
    });
  }

  public navigateToRegister(
    redirectLink?: string,
    email?: string,
    phoneNr?: string,
    name?: string,
    surname?: string,
    birthday?: string
  ): Promise<boolean> {
    const queryParams = {
      [QUERY_PARAMS.REDIRECT_LINK]: redirectLink || undefined,
      [QUERY_PARAMS.EMAIL]: email || undefined,
      [QUERY_PARAMS.PHONE_NUMBER]: phoneNr || undefined,
      [QUERY_PARAMS.REGISTER_NAME]: name || undefined,
      [QUERY_PARAMS.REGISTER_SURNAME]: surname || undefined,
      [QUERY_PARAMS.REGISTER_BIRTHDAY]: birthday || undefined,
    };

    return this.router.navigate([this.authRegistrationPersonalDataPath], {
      queryParams,
    });
  }

  public navigateToRegisterSecretData(
    name: string,
    surname: string,
    birthday?: string,
    redirectLink?: string,
    email?: string,
    phoneNr?: string
  ): Promise<boolean> {
    const queryParams = {
      [QUERY_PARAMS.REGISTER_NAME]: name || undefined,
      [QUERY_PARAMS.REGISTER_SURNAME]: surname || undefined,
      [QUERY_PARAMS.REGISTER_BIRTHDAY]: birthday || undefined,
      [QUERY_PARAMS.REDIRECT_LINK]: redirectLink || undefined,
      [QUERY_PARAMS.EMAIL]: email || undefined,
      [QUERY_PARAMS.PHONE_NUMBER]: phoneNr || undefined,
    };

    return this.router.navigate([this.authRegistrationSecretDataPath], {
      queryParams,
    });
  }

  public navigateToRegisterPin(
    challengeId: string,
    email: string,
    redirectLink?: string
  ): Promise<boolean> {
    const queryParams = {
      [QUERY_PARAMS.REGISTER_CHALLENGE_ID]: challengeId || undefined,
      [QUERY_PARAMS.EMAIL]: email || undefined,
      [QUERY_PARAMS.REDIRECT_LINK]: redirectLink || undefined,
    };

    return this.router.navigate([this.authRegistrationPinPath], {
      queryParams,
    });
  }

  public updateRegisterPinUrl(
    challengeId: string,
    email: string,
    redirectLink?: string
  ): Promise<boolean> {
    const queryParams = {
      [QUERY_PARAMS.REGISTER_CHALLENGE_ID]: challengeId || undefined,
      [QUERY_PARAMS.EMAIL]: email || undefined,
      [QUERY_PARAMS.REDIRECT_LINK]: redirectLink || undefined,
    };

    return this.router.navigate([this.authRegistrationPinPath], {
      queryParams,
      replaceUrl: true,
    });
  }

  public navigateToRegisterSuccess(redirectLink?: string): Promise<boolean> {
    const queryParams = {
      [QUERY_PARAMS.REDIRECT_LINK]: redirectLink || undefined,
    };

    return this.router.navigate([this.authRegistrationSuccessPath], {
      queryParams,
    });
  }

  // endregion

  // region FEEDBACK PROCESS
  public navigateToFeedbackInternalDetails(
    urlNameOrReviewUuid: string,
    star?: number
  ): Promise<boolean> {
    const queryParams = {
      [QUERY_PARAMS.INTERNAL_REVIEW_STAR]: star || undefined,
    };

    return this.router.navigate(
      [
        this.feedbackInternalDetailsPath.replace(
          `:${URL_PARAMETER.URL_NAME_OR_REVIEW_UUID}`,
          urlNameOrReviewUuid
        ),
      ],
      { queryParams }
    );
  }

  public navigateToFeedbackInternalSuccess(
    salonUrlName: string
  ): Promise<boolean> {
    return this.router.navigate([
      this.feedbackInternalSuccessPath.replace(
        `:${URL_PARAMETER.URL_NAME}`,
        salonUrlName
      ),
    ]);
  }

  // endregion

  // region PUBLIC
  public navigateToHome(): Promise<boolean> {
    return this.router.navigate([this.publicHomePath]);
  }

  public navigateToImprint(): Promise<boolean> {
    return this.router.navigate([this.publicImprintPath]);
  }

  public navigateToPrivacyPolicy(): Promise<boolean> {
    return this.router.navigate([this.publicPrivacyPolicyPath]);
  }

  public navigateToSalonSearchResult(queryParams: any): Promise<boolean> {
    return this.router.navigate([this.publicSalonSearchResultPath], {
      queryParams,
    });
  }

  public navigateToSalonDetails(boUrlName: string): Promise<boolean> {
    return this.router.navigate([
      this.publicSalonDetailsPath.replace(
        `:${URL_PARAMETER.URL_NAME}`,
        boUrlName
      ),
    ]);
  }

  public navigateToTopSalons(): Promise<boolean> {
    return this.router.navigate([this.publicTopSalonsPath]);
  }

  public navigateToTopSalonsCity(citySlug: string): Promise<boolean> {
    return this.router.navigate([
      this.publicTopSalonsCityPath.replace(`:${URL_PARAMETER.CITY}`, citySlug),
    ]);
  }

  public updateSalonDetails(
    boUrlName: string,
    selectedProductIds: string
  ): Promise<boolean> {
    const queryParams = {
      [QUERY_PARAMS.SELECTED_SERVICES]: selectedProductIds || undefined,
    };

    return this.router.navigate(
      [
        this.publicSalonDetailsPath.replace(
          `:${URL_PARAMETER.URL_NAME}`,
          boUrlName
        ),
      ],
      {
        queryParams,
        replaceUrl: true,
      }
    );
  }

  public navigateToSalonBookingConfig(
    boUrlName: string,
    selectedProductIds?: string
  ): Promise<boolean> {
    const queryParams = {
      [QUERY_PARAMS.SELECTED_SERVICES]: selectedProductIds || undefined,
    };

    return this.router.navigate(
      [
        this.publicSalonBookingConfigPath.replace(
          `:${URL_PARAMETER.URL_NAME}`,
          boUrlName
        ),
      ],
      {
        queryParams,
      }
    );
  }

  public navigateToGoogleSalonDetails(placeId: string): Promise<boolean> {
    return this.router.navigate([
      this.publicGoogleSalonDetailsPath.replace(
        `:${URL_PARAMETER.PLACE_ID}`,
        placeId
      ),
    ]);
  }

  public navigateToPublicAppointmentDetails(linkId: string): Promise<boolean> {
    return this.router.navigate(
      [
        this.publicAppointmentDetailsPath.replace(
          `:${URL_PARAMETER.BOOKING_UUID}`,
          linkId
        ),
      ],
      {
        replaceUrl: true,
      }
    );
  }

  public navigateToPartnerLandingPage(): void {
    this.forceRedirectToUrl('https://partner.beprobooking.com');
  }

  // endregion

  // region PRIVATE
  public navigateToUserProfile(): Promise<boolean> {
    return this.router.navigate([this.privateUserProfilePath]);
  }

  public navigateToSalonVisited(): Promise<boolean> {
    return this.router.navigate([this.privateSalonVisitedPath]);
  }

  public navigateToAppointmentHistory(): Promise<boolean> {
    return this.router.navigate([this.privateAppointmentHistoryPath]);
  }

  public navigateToAppointmentDetails(
    appointmentId: number,
    previousPath?: string
  ): Promise<boolean> {
    const queryParams = {
      [QUERY_PARAMS.PREVIOUS_ROUTE_PATH]: previousPath || undefined,
    };

    return this.router.navigate(
      [
        this.privateAppointmentDetailsPath.replace(
          `:${URL_PARAMETER.APPOINTMENT_ID}`,
          appointmentId.toString()
        ),
      ],
      {
        queryParams,
        replaceUrl: !previousPath?.length,
      }
    );
  }

  public navigateToQrCheckinConfig(
    boUrlName: string,
    selectedProductIds?: string
  ): Promise<boolean> {
    const queryParams = {
      [QUERY_PARAMS.SELECTED_SERVICES]: selectedProductIds || undefined,
    };

    return this.router.navigate(
      [
        this.privateQrCheckinConfigPath.replace(
          `:${URL_PARAMETER.URL_NAME}`,
          boUrlName
        ),
      ],
      {
        queryParams,
        replaceUrl: true,
      }
    );
  }

  public navigateToQrCheckinSuccess(boUrlName: string): Promise<boolean> {
    return this.router.navigate(
      [
        this.privateQrCheckinSuccessPath.replace(
          `:${URL_PARAMETER.URL_NAME}`,
          boUrlName
        ),
      ],
      {
        replaceUrl: true,
      }
    );
  }

  // endregion

  // to prevent hacker change host of the redirect-URL to scam website
  private checkAndCorrectDomainOfUrl(url: string): string {
    try {
      const urlObj = new URL(url);
      urlObj.host = window.location.host;
      return urlObj.href;
    } catch (e) {
      return url;
    }
  }
}
